import { orderBy, uniqueId } from 'lodash';

import { TRENDING_FLAG, sortAttAsc, sortAttDesc, sortAttName, sortAttPrice, sortAttRank } from '@common/constants';
import { ProductType, SortBy } from '@common/enums';
import { ApplianceModel, ApplianceSource, ApplianceType } from '@common/models';
import { addMockImage, convertProductsToProductDetailsList } from '@common/utils';
import { createSelector } from '@reduxjs/toolkit';

export const convertSinksToProductDetails = convertProductsToProductDetailsList(ProductType.SINK);
export const convertSinkLaborToProductDetails = convertProductsToProductDetailsList(ProductType.SINK_LABOR);

export const sortSinks = (sinks: ApplianceModel[], sortBy?: SortBy): ApplianceModel[] => {
    switch (sortBy) {
        case SortBy.HighToLow:
            return orderBy(sinks, [sortAttPrice, sortAttRank], [sortAttDesc, sortAttAsc]);
        case SortBy.LowToHigh:
            return orderBy(sinks, [sortAttPrice, sortAttRank], [sortAttAsc, sortAttAsc]);
        case SortBy.NameAZ:
            return orderBy(sinks, [sortAttName], [sortAttAsc]);
        case SortBy.NameZA:
            return orderBy(sinks, [sortAttName], [sortAttDesc]);
        case SortBy.TrendingNow: {
            const onTrendSinks = orderBy(
                sinks.filter((sink: ApplianceModel) => sink.features.find((item) => item.name.match(TRENDING_FLAG))),
                [sortAttRank],
                [sortAttAsc]
            );

            const otherSinks = orderBy(
                sinks.filter(
                    (sink: ApplianceModel) => !sink.features.find((item) => item.name.match(TRENDING_FLAG)) && sink
                ),
                [sortAttRank],
                [sortAttAsc]
            );

            return [...onTrendSinks, ...otherSinks];
        }
        default:
            return orderBy(sinks, [sortAttName], [sortAttAsc]);
    }
};

/**
 * Corrects a given productSourceValue case
 * @param originalProductSource
 * @param correctProductSource
 */
const fixProductSourceCase = (originalProductSource: string, correctProductSource: string) => {
    return originalProductSource
        ? originalProductSource.replace(new RegExp(correctProductSource, 'gi'), correctProductSource)
        : correctProductSource;
};

type SelectorCallback<T> = (
    data: ApplianceModel[],
    productSource: ApplianceSource,
    applianceType?: ApplianceType,
    shouldAddProductSourceToFeatures?: boolean
) => T;

export const makeSinksSelector = () =>
    createSelector<
        [
            SelectorCallback<ApplianceModel[]>,
            SelectorCallback<ApplianceSource>,
            SelectorCallback<ApplianceType | undefined>,
            SelectorCallback<boolean | undefined>
        ],
        ApplianceModel[]
    >(
        [
            (data) => data,
            (_, productSource) => productSource,
            (_, __, applianceType) => applianceType,
            (_, __, ___, shouldAddProductSourceToFeatures) => shouldAddProductSourceToFeatures,
        ],
        (data, productSource, applianceType, shouldAddProductSourceToFeatures = false) => {
            return data
                ?.map((sink) => ({
                    ...sink,
                    uuid: sink.uuid ?? uniqueId(),
                    productSource,
                    applianceType: sink.applianceType ?? applianceType,
                    features: shouldAddProductSourceToFeatures
                        ? [
                              ...(sink.features ?? []),
                              {
                                  name: 'productSource',
                                  description: 'Product Source',
                                  position: 12,
                                  value: sink.productSource // TODO Should we just use the given parameter productSource and ignore what the API provides?
                                      ? fixProductSourceCase(sink.productSource, ApplianceSource.PROGRAM) // fix the case on program products
                                      : productSource,
                              },
                          ]
                        : sink.features ?? [], //TODO: sink.features in data may actually be undefined despite current typing saying otherwise. Fixing types required
                }))
                ?.map(addMockImage);
        }
    );
